import { api, download, listPages, paged } from '../console/api.ts';
import {
  carrierName,
  carrierOffers,
  inTesting,
  offeredNow,
  planName,
} from '../console/carriers.ts';
import { empty, field, idRef, loadMore, pageHead, pill, quoteRef } from '../console/components.ts';
import { ApiError, explain, InputError } from '../console/errors.ts';
import {
  cap,
  DECISION,
  date,
  esc,
  FREQUENCY,
  fmt,
  icon,
  initials,
  iso,
  type Money,
  q,
  quoteNumber,
  REASON,
  shortId,
  when,
} from '../console/format.ts';
import { followRequest, issuanceScope } from '../console/retry-keys.ts';
import { type Actions, type ClickAction, render } from '../console/router.ts';
import { canQuote, me } from '../console/session.ts';
import { $, act, textInput, toast } from '../console/ui.ts';
import {
  checkboxControl,
  checkedValues,
  dateControl,
  moneyControl,
  numberControl,
  optionTag,
  REQUIRED_HINT,
  REQUIRED_MARK,
  requiredLabel,
  selectControl,
  textControl,
} from '../controls.ts';
import {
  CHANNEL_LABEL,
  type Comparison,
  carrierOption,
  filterQuery,
  NO_FILTER,
  optionDetail,
  optionKey,
  QUOTE_LOST_REASON,
  type QuoteFilter,
  type QuoteOptionsView,
  type QuoteRow,
  quoteList,
  quoteTools,
  statusPill,
  type VersionComparison,
} from './cotizacion-detalle.ts';
import { focusPolicy } from './polizas.ts';

/**
 * «Cotizar»: the product's questionnaire, one request to every carrier, the results side by side,
 * and issuing (or opening the underwriting file) from the chosen option. Also the quote on screen
 * that other views start (from a customer or a prospect).
 */

export type Option = {
  carrierOptionCode: string;
  decision: string;
  premium: {
    gross: Money;
    net: Money;
    installments: { count: number; first: Money; regular: Money };
  } | null;
  commission: Money | null;
};
type CarrierQuote = {
  carrier: string;
  name: string;
  status: 'QUOTED' | 'MISSING_FIELDS' | 'ERROR' | 'WORK_ORDER';
  /** The carrier has no quoter for the line: Nubi opened a work order to its desk. */
  workOrder?: { id: string; dueAt: string; message: string };
  result?: {
    decision: string;
    reasons: { carrierCode: string; category: string }[];
    validUntil: string;
    options: Option[];
    members?: { personId: string; decision: string; exclusions: string[] }[];
    /** Priced by Nubi from the carrier's published RuleSet (not confirmed by the carrier). */
    indicative?: boolean;
    ruleSet?: { id: string; version: number };
  };
  missing?: string[];
  error?: string;
};
/** A canonical quote request as the API keeps it (any line of the catalog). */
export type QuoteReq = {
  productLine: string;
  effectiveDate: string;
  paymentFrequency: string;
  [field: string]: unknown;
};
/** The quote on screen: its saved id and version, the previous version's answers, if bound. */
let lastQuote: {
  request: QuoteReq;
  quotes: CarrierQuote[];
  quoteId: string;
  /** The agency's number of the quote (COT-000123, #259); absent in older answers. */
  number?: number | null;
  version: number;
  previous: CarrierQuote[] | null;
  bound: string | null;
  /** The quote's own agent, when known (an opened quote says it). */
  agentId?: string | null;
} | null = null;
export type QuoteSummary = {
  id: string;
  /** The agency's number of the quote (COT-000123, #259). */
  number?: number | null;
  label: string;
  agentId: string | null;
  partyId?: string | null;
  versions: number;
  updatedAt: string;
  bestPremium: Money | null;
  binding: { policyNumber: string; carrier: string; option: string } | null;
};

/** The list's search and filters (#314). */
let quoteFilter: QuoteFilter = { ...NO_FILTER };
/** The options picked to compare and propose, of the quote on screen and its version. */
let picks: { of: string; keys: string[] } = { of: '', keys: [] };
let comparison: Comparison | null = null;
let versionCompared: VersionComparison | null = null;
/** At most this many options in a comparison (the proposal's columns). */
const MAX_PICKS = 4;
const pickedOf = (quoteId: string, version: number) => `${quoteId}#${version}`;

// Catalog (ADR-0006): the lines the agency's carriers offer and the questionnaire of each.
type Choice = { value: string; label: string };
type QField = {
  path: string;
  label: string;
  noun: string;
  help?: string;
  required: boolean;
  default?: unknown;
  type: 'text' | 'integer' | 'decimal' | 'money' | 'date' | 'select' | 'boolean' | 'group';
  min?: number | string;
  max?: number | string;
  maxLength?: number;
  choices?: number[];
  /** A money amount also answered in another currency, as Money, from that currency's list. */
  otherCurrencies?: { currency: string; choices: number[] }[];
  options?: Choice[];
  multiple?: boolean;
  unit?: string;
  decimals?: number;
  itemLabel?: string;
  minItems?: number;
  maxItems?: number;
  fields?: QField[];
};
type CatalogLine = {
  productLine: string;
  name: string;
  family: string;
  familyName: string;
  description: string;
  questionnaire: {
    id: string;
    version: number;
    fields: QField[];
    documents: { code: string; label: string }[];
  };
  /** `testing`: a carrier in testing the agency pilots («En pruebas», #283). */
  carriers: { id: string; name: string; testing?: boolean }[];
};
type Answers = Record<string, unknown>;

let catalog: Promise<CatalogLine[]> | null = null;
export const loadCatalog = () => {
  catalog ??= api<{ lines: CatalogLine[] }>('GET', '/v1/catalog').then((r) => r.lines);
  catalog.catch(() => {
    catalog = null;
  });
  return catalog;
};
/** The product chosen in «Cotizar». */
let quoteLine = 'AUTO';
/** What the person typed in a generated form, kept across re-renders (add a person, errors). */
let quoteDraft: Answers | null = null;
/** Validation errors of the last attempt, by answer path ("insuredPersons[0].birthDate"). */
let fieldErrors: Record<string, string> = {};

/** Legends of the generated form, by the first segment of the canonical path. */
const SECTION: Record<string, string> = {
  vehicle: 'Vehículo',
  mainDriver: 'Conductor',
  health: 'Plan',
  life: 'Suma y plazo',
  accident: 'Suma asegurada',
  property: 'Inmueble',
  business: 'Negocio',
  coverageSelection: 'Coberturas',
  surety: 'Fianza',
};
const leafOf = (path: string) => path.split('[].').pop() ?? path;
const slug = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, '-');
const readPath = (source: unknown, path: string) =>
  path
    .split('.')
    .reduce<unknown>(
      (o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined),
      source,
    );
/**
 * Money answers are minor units; a canonical Money reads back as its amount, or stays a Money
 * when it is in one of the field's other currencies (a deductible in dollars).
 */
const answerOf = (f: QField, v: unknown) =>
  f.type === 'money' &&
  v &&
  typeof v === 'object' &&
  !f.otherCurrencies?.some((o) => o.currency === (v as Money).currency)
    ? (v as Money).amountMinor
    : v;
/** A money choice as an option: "250000" in quetzales, "USD:250000" in another currency. */
const moneyOption = (amount: number, currency: string, v: unknown) => {
  const chosen =
    currency === 'GTQ'
      ? amount === v
      : !!v &&
        typeof v === 'object' &&
        (v as Money).currency === currency &&
        (v as Money).amountMinor === amount;
  return optionTag(
    currency === 'GTQ' ? String(amount) : `${currency}:${amount}`,
    q(amount, currency),
    chosen,
  );
};
/** The answers a saved request gives back, to requote it in the same form. */
function answersFromRequest(fields: QField[], request: unknown): Answers {
  const answers: Answers = {};
  for (const f of fields) {
    const v = readPath(request, f.path);
    if (v === undefined) continue;
    answers[f.path] =
      f.type === 'group' && Array.isArray(v)
        ? v.map((item) =>
            Object.fromEntries(
              (f.fields ?? []).map((c) => [
                leafOf(c.path),
                answerOf(c, readPath(item, leafOf(c.path))),
              ]),
            ),
          )
        : answerOf(f, v);
  }
  return answers;
}

/** One control of the generated form; `key` is its answer path, used for its error. */
function control(f: QField, value: unknown, key: string, id: string) {
  const v = value === undefined ? f.default : value;
  const err = fieldErrors[key];
  const invalid = {
    'aria-invalid': err ? 'true' : undefined,
    'aria-describedby': err ? `${id}-err` : undefined,
  };
  const aria = { ...invalid, required: f.required };
  const data = { 'data-key': leafOf(f.path) };
  const hint = f.help ? `<small class="hint">${esc(f.help)}</small>` : '';
  const error = err ? `<small class="field-error" id="${id}-err">${esc(err)}</small>` : '';
  const label = `${f.label}${f.unit ? ` (${f.unit})` : ''}`;
  // A required answer carries the mark in its label (#235, B5).
  const named = f.required ? { labelHtml: requiredLabel(label) } : { label };
  const labelled = (input: string) => `<div class="field">${input}${hint}${error}</div>`;
  switch (f.type) {
    case 'boolean':
      return `<div class="field">${checkboxControl({ id, class: 'check', ...data, checked: v === true, ...invalid }, esc(f.label))}${hint}${error}</div>`;
    case 'select':
      if (f.multiple) {
        const chosen = Array.isArray(v) ? (v as string[]) : [];
        return `<fieldset class="checks" id="${id}" data-key="${esc(leafOf(f.path))}" data-multiple${err ? ` aria-describedby="${id}-err"` : ''}><legend>${esc(f.label)}${f.required ? ` ${REQUIRED_MARK}` : ''}</legend>
          ${(f.options ?? []).map((o) => checkboxControl({ class: 'check', value: o.value, checked: chosen.includes(o.value) }, esc(o.label))).join('')}${hint}${error}</fieldset>`;
      }
      return labelled(
        selectControl({ ...named, id, ...data, ...aria }, [
          ...(f.required && v !== undefined ? [] : [{ value: '', label: 'Elige una opción' }]),
          ...(f.options ?? []).map((o) => ({
            value: o.value,
            label: o.label,
            selected: o.value === v,
          })),
        ]),
      );
    case 'money':
      if (f.choices)
        return labelled(
          selectControl(
            { ...named, id, ...data, ...aria },
            `${f.choices.map((c) => moneyOption(c, 'GTQ', v)).join('')}${(f.otherCurrencies ?? [])
              .flatMap((o) => o.choices.map((c) => moneyOption(c, o.currency, v)))
              .join('')}`,
          ),
        );
      return labelled(
        moneyControl({
          ...named,
          id,
          ...data,
          type: 'number',
          step: '0.01',
          min: 0,
          value: typeof v === 'number' ? v / 100 : undefined,
          ...aria,
        }),
      );
    case 'integer':
    case 'decimal':
      return labelled(
        numberControl({
          ...named,
          id,
          ...data,
          inputmode: f.type === 'integer' ? 'numeric' : 'decimal',
          step: f.type === 'integer' ? 1 : 10 ** -(f.decimals ?? 1),
          min: f.min,
          max: f.max,
          value: typeof v === 'number' ? v : undefined,
          ...aria,
        }),
      );
    case 'date':
      return labelled(
        dateControl({
          ...named,
          id,
          ...data,
          value: typeof v === 'string' ? v : undefined,
          ...aria,
        }),
      );
    default:
      return labelled(
        textControl({
          ...named,
          id,
          ...data,
          type: 'text',
          maxlength: f.maxLength,
          value: typeof v === 'string' ? v : undefined,
          ...aria,
        }),
      );
  }
}

/** A repeatable group (insured persons): one block per item, add and remove. */
function groupControl(f: QField, value: unknown) {
  const items = Array.isArray(value) ? (value as Answers[]) : [];
  const min = f.minItems ?? 0;
  const max = f.maxItems ?? 1;
  // An optional group (the driver's claims) starts empty; a required one shows its first item.
  const shown: Answers[] = items.length ? items : Array.from({ length: min }, () => ({}));
  const err = fieldErrors[f.path];
  const label = (f.itemLabel ?? 'Elemento').toLowerCase();
  return `<fieldset class="group span-all" data-group="${esc(f.path)}"><legend>${esc(f.label)}</legend>
    ${f.help ? `<p class="hint">${esc(f.help)}</p>` : ''}${err ? `<p class="field-error" role="alert">${esc(err)}</p>` : ''}
    ${shown
      .map(
        (item, i) => `<div class="group-item" data-group-item="${esc(f.path)}">
        <div class="group-head"><strong>${esc(f.itemLabel ?? '')} ${i + 1}</strong>
        ${i >= min ? `<button type="button" class="btn sm" data-remove-item="${esc(f.path)}" data-index="${i}" aria-label="Quitar ${esc(label)} ${i + 1}">Quitar</button>` : ''}</div>
        <div class="group-grid">${(f.fields ?? [])
          .map((c) =>
            control(
              c,
              item[leafOf(c.path)],
              c.path.replace('[]', `[${i}]`),
              `qa-${slug(f.path)}-${i}-${slug(leafOf(c.path))}`,
            ),
          )
          .join('')}</div></div>`,
      )
      .join('')}
    ${shown.length < max ? `<div><button type="button" class="btn sm" data-add-item="${esc(f.path)}">Agregar ${esc(label)}</button></div>` : ''}</fieldset>`;
}

/** The form generated from a line's questionnaire, one fieldset per part of the request. */
function questionnaireForm(line: CatalogLine, answers: Answers) {
  const sections = new Map<string, QField[]>();
  for (const f of line.questionnaire.fields) {
    const key = f.type === 'group' ? f.path : (f.path.split('.')[0] ?? f.path);
    sections.set(key, [...(sections.get(key) ?? []), f]);
  }
  return [...sections.entries()]
    .map(([key, fields]) => {
      const group = fields.find((f) => f.type === 'group');
      if (group) return groupControl(group, answers[group.path]);
      return `<fieldset><legend>${esc(SECTION[key] ?? key)}</legend>${fields
        .map((f) => control(f, answers[f.path], f.path, `qa-${slug(f.path)}`))
        .join('')}</fieldset>`;
    })
    .join('');
}

/** Reads one control back into an answer (money in minor units; blanks as missing). */
function readControl(f: QField, root: ParentNode): unknown {
  const el = root.querySelector<HTMLInputElement & HTMLSelectElement>(
    `[data-key="${leafOf(f.path)}"]`,
  );
  if (!el) return undefined;
  if (f.type === 'select' && f.multiple) return checkedValues(el, 'wa-checkbox');
  if (f.type === 'boolean') return el.checked;
  const raw = String(el.value ?? '').trim();
  if (!raw) return undefined;
  if (f.type === 'money') {
    const other = /^([A-Z]{3}):(\d+)$/.exec(raw);
    if (other) return { amountMinor: Number(other[2]), currency: other[1] };
    const n = Number(raw.replace(/[Q,\s]/gi, ''));
    if (!Number.isFinite(n)) return raw;
    return f.choices ? n : Math.round(n * 100);
  }
  if (f.type === 'integer' || f.type === 'decimal') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

function readAnswers(line: CatalogLine): Answers {
  const answers: Answers = {};
  const form = $('#q-dynamic');
  for (const f of line.questionnaire.fields) {
    if (f.type === 'group') {
      answers[f.path] = [
        ...form.querySelectorAll<HTMLElement>(`[data-group-item="${CSS.escape(f.path)}"]`),
      ].map((item) =>
        Object.fromEntries(
          (f.fields ?? []).flatMap((c) => {
            const v = readControl(c, item);
            return v === undefined ? [] : [[leafOf(c.path), v]];
          }),
        ),
      );
      continue;
    }
    const v = readControl(f, form);
    if (v !== undefined) answers[f.path] = v;
  }
  return answers;
}

/** "insuredPersons[].smoker" → "Fuma" (what the carrier asks for, in the form's words). */
function fieldLabel(path: string, fields: QField[] = []): string {
  for (const f of fields) {
    if (f.path === path) return f.label;
    const inner = f.fields && fieldLabel(path, f.fields);
    if (inner && inner !== path) return inner;
  }
  return path;
}

export async function cotizar() {
  if (!canQuote())
    return (
      pageHead('Cotizar', '') +
      empty(
        'lock',
        'Tu rol no cotiza',
        'Pide a dirección, administración o a un agente que cotice por ti.',
        '',
        2,
      )
    );
  const open = lastQuote && !lastQuote.bound ? lastQuote : null;
  if (lastQuote && picks.of !== pickedOf(lastQuote.quoteId, lastQuote.version)) {
    // Another quote (or version) on screen: nothing picked or compared yet.
    picks = { of: pickedOf(lastQuote.quoteId, lastQuote.version), keys: [] };
    comparison = null;
    versionCompared = null;
  }
  const [recentPage, lines, uw, agents, detail] = await Promise.all([
    paged<QuoteRow>('cotizaciones', `/v1/quotes${filterQuery(quoteFilter)}`, 10).catch(() => ({
      items: [] as QuoteRow[],
      more: false,
    })),
    loadCatalog(),
    // Which options need an underwriting file before issuing (and the files already open).
    open
      ? api<{ options: UwOption[] }>(
          'GET',
          `/v1/quotes/${encodeURIComponent(open.quoteId)}/underwriting?version=${open.version}`,
        )
          .then((r) => r.options)
          .catch(() => null)
      : Promise.resolve(null),
    // Management issues on behalf of an agent (who sells it and earns its commission) and
    // filters the list by agent.
    !me?.agentId
      ? api<{ agentId: string }[]>('GET', '/v1/agents')
          .then((a) => [...new Set(a.map((x) => x.agentId))])
          .catch(() => [] as string[])
      : Promise.resolve([] as string[]),
    // Each option's coverages and premium broken down, and where its proposal can go (#314).
    lastQuote
      ? api<QuoteOptionsView>(
          'GET',
          `/v1/quotes/${encodeURIComponent(lastQuote.quoteId)}/options?version=${lastQuote.version}`,
        )
          .then((d) => (Array.isArray(d?.carriers) ? d : null))
          .catch(() => null)
      : Promise.resolve(null),
  ]);
  const recent = recentPage.items;
  sellingAgents = {
    list: agents,
    selected: open?.agentId ?? recent.find((q) => q.id === open?.quoteId)?.agentId ?? null,
  };
  const head = pageHead(
    'Cotizar',
    'Elige el producto, responde lo que piden las aseguradoras y compara prima, cuotas y comisión de cada una.',
  );
  const listHtml = quoteList(recent, recentPage.more, quoteFilter, {
    carriers: Object.keys(carrierOffers).map(carrierOption),
    lines: lines.map((l) => l.productLine),
    agents: me?.agentId ? null : agents,
  });
  const tools = detail
    ? quoteTools(detail, {
        picked: picks.keys,
        comparison,
        diff: versionCompared,
        canPropose: canQuote(),
        canClose: canQuote(),
      })
    : '';
  if (!lines.length) {
    // Its carriers are there but the platform offers none now (#283): say so, and keep the saved
    // quotes readable (history), never issuable.
    const turnedOff = Object.values(carrierOffers).some((o) => !o.offered);
    return (
      head +
      (turnedOff
        ? empty(
            'lock',
            'Tus aseguradoras no cotizan ahora en Nubi',
            'La administración de Nubi apagó o tiene en pruebas a tus aseguradoras: no se cotizan ni se emiten pólizas nuevas con ellas. Lo ya emitido sigue igual: cobros, renovaciones, endosos y conciliación.',
            '',
            2,
          )
        : empty(
            'inbox',
            'Tu agencia no tiene productos para cotizar',
            'Cuando la agencia tenga un nombramiento con una aseguradora, sus productos aparecerán aquí.',
            '',
            2,
          )) +
      `<div id="quote-results">${lastQuote ? `${quoteResults(lastQuote.quotes, lastQuote.previous, lastQuote.version, !!lastQuote.bound || detail?.status === 'LOST', [], uw, detail, picks.keys)}${tools}` : ''}</div>${listHtml}`
    );
  }
  if (lastQuote) quoteLine = lastQuote.request.productLine;
  const line = lines.find((l) => l.productLine === quoteLine) ?? (lines[0] as CatalogLine);
  quoteLine = line.productLine;
  const r = lastQuote?.request;
  const start = r?.effectiveDate ?? iso(new Date(Date.now() + 7 * 86_400_000));
  // A lost quote is final: it is not requoted (#314).
  const lost = detail?.status === 'LOST';
  const requoting = !!lastQuote && !lastQuote.bound && !lost;
  const families = [...new Set(lines.map((l) => l.familyName))];
  const picker = `<div class="line-picker">
      ${field(
        'Producto',
        selectControl(
          { id: 'q-line', 'aria-describedby': 'q-line-help' },
          families
            .map(
              (fam) =>
                `<small class="option-group">${esc(fam)}</small>${lines
                  .filter((l) => l.familyName === fam)
                  .map((l) => optionTag(l.productLine, l.name, l.productLine === line.productLine))
                  .join('')}`,
            )
            .join(''),
        ),
      )}
      <p class="muted small" id="q-line-help">${esc(line.description)} Cotizan: ${esc(line.carriers.map((c) => (c.testing ? `${c.name} (en pruebas)` : c.name)).join(', '))}.</p></div>`;
  const policyFieldset = `<fieldset><legend>Póliza</legend>
        ${field(
          'Forma de pago',
          selectControl(
            { id: 'q-freq' },
            Object.entries(FREQUENCY).map(([k, v]) => ({
              value: k,
              label: v,
              selected:
                k ===
                (r?.paymentFrequency ?? (line.productLine === 'AUTO' ? 'QUARTERLY' : 'ANNUAL')),
            })),
          ),
        )}
        ${field('Inicio de vigencia', dateControl({ id: 'q-start', value: start, required: true }), { required: true })}
      </fieldset>`;
  const body = `${REQUIRED_HINT}<div class="form-grid"><div id="q-dynamic" class="dynamic">${questionnaireForm(
    line,
    quoteDraft ?? (r ? answersFromRequest(line.questionnaire.fields, r) : {}),
  )}</div>${policyFieldset}</div>
      <p class="muted small docs">${icon('policy')}<span>Para emitir, la aseguradora pedirá: ${esc(line.questionnaire.documents.map((d) => (/^[A-Z]{2}/.test(d.label) ? d.label : d.label.charAt(0).toLowerCase() + d.label.slice(1))).join(', '))}.</span></p>`;
  const form = `<form id="quote-form" class="card" novalidate>
    ${
      lastQuote
        ? `<div class="card-head"><h2>${esc(recent.find((q) => q.id === lastQuote?.quoteId)?.label ?? 'Cotización')}</h2>
      <span class="muted small">${quoteRef({ id: lastQuote.quoteId, number: lastQuote.number })} · versión ${lastQuote.version}</span><span class="spacer"></span>
      ${lastQuote.bound ? pill('ok', `Emitida · ${lastQuote.bound}`) : statusPill(detail?.status ?? 'OPEN')}</div>`
        : ''
    }
    ${picker}
    ${body}
    ${
      requoting
        ? `<div class="form-foot"><div class="field grow-field">${textControl({ label: 'Motivo de la recotización', id: 'q-reason', maxlength: 300, placeholder: 'El cliente actualizó el valor comercial' })}</div>
      <div class="order-buttons"><button type="button" class="btn" data-new-quote>Nueva cotización</button>
      <button class="btn primary lg" type="submit" id="quote-submit" data-requote>${icon('bolt')}Recotizar (versión ${(lastQuote?.version ?? 0) + 1})</button></div></div>`
        : `<div class="form-foot"><span class="muted small">Una sola solicitud a todas tus aseguradoras; cada una responde con sus propias reglas. La cotización queda guardada.</span>
      <div class="order-buttons">${lastQuote ? '<button type="button" class="btn" data-new-quote>Nueva cotización</button>' : ''}
      <button class="btn primary lg" type="submit" id="quote-submit">${icon('bolt')}Cotizar con todas</button></div></div>`
    }
  </form>`;
  const recentHtml = listHtml;
  const forParty =
    quoteParty && !requoting
      ? `<p class="card lead-quoting">${icon('users')}<span>Cotizando para el cliente <strong>${esc(quoteParty.name)}</strong> <span class="muted">${idRef(quoteParty.id)}</span>: la cotización queda en su ficha y el contratante viene de sus datos.</span>
        <button type="button" class="btn sm" data-party-quote-drop>Cotizar sin cliente</button></p>`
      : '';
  const toParty =
    lastQuote && (quoteParty || holderOf(lastQuote.request))
      ? `<p class="quote-party"><button type="button" class="btn sm" data-party-of-quote="${esc(lastQuote.quoteId)}">${icon('users')}Ver ficha del cliente</button></p>`
      : '';
  const forLead =
    quoteLead && !requoting
      ? `<p class="card lead-quoting">${icon('users')}<span>Cotizando para el prospecto <strong>${esc(quoteLead.name)}</strong> <span class="muted">${idRef(quoteLead.id)}</span>: al cotizar queda como prospecto cotizado con esta cotización.</span>
        <button type="button" class="btn sm" data-lead-quote-drop>Cotizar sin prospecto</button></p>`
      : '';
  return `${head}${forLead}${forParty}${form}${toParty}
    <div id="quote-results">${lastQuote ? `${quoteResults(lastQuote.quotes, lastQuote.previous, lastQuote.version, !!lastQuote.bound || lost, line.questionnaire.fields, uw, detail, picks.keys)}${tools}${holderBlock()}` : ''}</div>${recentHtml}`;
}

/** Who contracts the policy (the carrier issues it in this person's name). */
export type Holder = {
  fullName: string;
  nationalId: string;
  taxId: string;
  email: string;
  phone?: string;
  /** The PEP question of the carriers' application forms (G&T asks it for gastos médicos and vida). */
  politicallyExposed?: boolean;
};
const holderOf = (request: unknown) => (request as { policyholder?: Holder }).policyholder;

/**
 * The plan chosen with «Emitir» in the comparison (#238, A6): the policyholder's form opens for
 * it, under the prices. It belongs to one version of the quote (a requote leaves it).
 */
let chosenPlan: { quoteId: string; version: number; carrier: string; option: string } | null = null;

/** What the policyholder's block says of the chosen plan: plan, carrier, premium, installments. */
export type ChosenPlan = {
  carrier: string;
  option: string;
  plan: string;
  carrierName: string;
  premium: string;
  installments: string;
  indicative: boolean;
};

/** The chosen plan of the quote on screen, as its answer priced it (null: none, or not there). */
export function chosenOf(
  quotes: CarrierQuote[],
  carrier: string,
  option: string,
): ChosenPlan | null {
  const c = quotes.find((x) => x.carrier === carrier);
  const o = c?.result?.options.find((x) => x.carrierOptionCode === option && x.premium);
  if (!c?.result || !o?.premium) return null;
  return {
    carrier,
    option,
    plan: planName(carrier, option),
    carrierName: c.name,
    premium: fmt(o.premium.gross),
    installments: `${o.premium.installments.count} × ${fmt(o.premium.installments.regular)}`,
    indicative: !!c.result.indicative,
  };
}

/** The chosen plan of the open quote (its version, not issued yet), or null. */
function openChosen(): ChosenPlan | null {
  const q = lastQuote;
  const c = chosenPlan;
  if (!q || q.bound || !c || c.quoteId !== q.quoteId || c.version !== q.version) return null;
  return chosenOf(q.quotes, c.carrier, c.option);
}

/** The chosen plan, summed up at the top of the policyholder's block. */
export function chosenSummary(p: ChosenPlan) {
  return `<div class="chosen-plan" id="chosen-plan" data-carrier="${esc(p.carrier)}" data-option="${esc(p.option)}">
    <div class="chosen-plan-what"><span class="eyebrow">Plan elegido</span>
    <p><strong>${esc(p.plan)}</strong> con ${esc(p.carrierName)}</p>
    <p class="small chosen-plan-price"><span class="premium">${esc(p.premium)}</span> al año · ${esc(p.installments)}${p.indicative ? ' · precio indicativo' : ''}</p></div>
    <button type="button" class="btn sm" data-change-plan>Cambiar de plan</button></div>`;
}

/**
 * The single button that issues the chosen plan (the one `bind` action, #219): «Emitir póliza»,
 * or «Pedir emisión» for an indicative price (the carrier confirms it).
 */
export function issueFoot(p: ChosenPlan) {
  return `<div class="form-foot" id="holder-foot"><span class="muted small">${
    p.indicative
      ? `${esc(p.carrierName)} confirma el precio indicativo y emite a nombre del contratante.`
      : `${esc(p.carrierName)} emite ${esc(p.plan)} a nombre del contratante.`
  }</span>
    <button type="button" class="btn primary lg" data-bind="${esc(p.carrier)}" data-carrier="${esc(p.carrier)}" data-option="${esc(p.option)}"${p.indicative ? ' data-indicative="1" title="Abre una orden de trabajo para que la aseguradora confirme el precio y emita"' : ''}>${icon('policy')}${p.indicative ? 'Pedir emisión' : 'Emitir póliza'}</button></div>`;
}

/** The policyholder's block of the open quote: only once a plan is chosen (#238). */
function holderBlock() {
  const p = openChosen();
  if (!p || !lastQuote) return '';
  return holderForm(
    holderDraft?.fullName
      ? (holderDraft as Holder)
      : (holderOf(lastQuote.request) ?? quoteHolder ?? undefined),
    (lastQuote.request as { productLine?: string }).productLine,
    p,
  );
}

/**
 * Opens the policyholder's block for the chosen plan, under the prices: a block already open
 * keeps what was typed and only changes its plan. The focus goes to «Nombre completo», in sight.
 */
function showHolder() {
  const p = openChosen();
  const results = document.querySelector('#quote-results');
  if (!p || !results) return;
  const form = document.querySelector('#holder-form');
  if (form) {
    $('#chosen-plan').outerHTML = chosenSummary(p);
    $('#holder-foot').outerHTML = issueFoot(p);
  } else results.insertAdjacentHTML('beforeend', holderBlock());
  scrollBelowBar($('#holder-form'));
  const name = $('#h-name') as HTMLElement & { updateComplete?: Promise<unknown> };
  void Promise.resolve(name.updateComplete).then(() => name.focus({ preventScroll: true }));
}

/** Asked before issuing; prefilled when the quote already says who contracts. */
/** Agents management can issue for, and the quote's own agent (preselected). */
let sellingAgents: { list: string[]; selected: string | null } = {
  list: [],
  selected: null,
};
/** The agent management chose in the policyholder form (agents sell their own). */
export const sellingAgent = () =>
  document.querySelector<HTMLSelectElement>('#h-agent')?.value || undefined;
/**
 * Whether «Agente que vende» must be chosen: the API's rule (policies.ts, POST /v1/policies:
 * the agent issuing, else the one named, else the quote's) seen from the form: whoever issues
 * is not an agent and the quote has no agent of its own.
 */
const agentRequired = () => !me?.agentId && !sellingAgents.selected;

function holderForm(h: Partial<Holder> | undefined, line: string | undefined, p: ChosenPlan) {
  const pep = h?.politicallyExposed;
  const opt = optionTag;
  const req = { required: true };
  return `<form id="holder-form" class="card" novalidate aria-labelledby="holder-title"><div class="card-head"><h2 id="holder-title">Contratante</h2><span class="spacer"></span>
    <span class="muted small">La aseguradora emite la póliza a nombre de esta persona.</span></div>
    ${chosenSummary(p)}
    ${REQUIRED_HINT}
    <div class="form-grid">
      <fieldset><legend>Persona</legend>
        ${field('Nombre completo', textControl({ id: 'h-name', maxlength: 120, autocomplete: 'off', value: h?.fullName ?? '' }), req)}
        ${field('DPI (CUI)', textControl({ id: 'h-cui', inputmode: 'numeric', maxlength: 15, placeholder: '13 dígitos', autocomplete: 'off', value: h?.nationalId ?? '' }), req)}
        ${field('NIT', textControl({ id: 'h-nit', maxlength: 15, placeholder: '1234567-8', autocomplete: 'off', value: h?.taxId ?? '' }), req)}
      </fieldset>
      <fieldset><legend>Contacto</legend>
        ${field('Correo', textControl({ id: 'h-email', type: 'email', autocomplete: 'off', value: h?.email ?? '' }), req)}
        ${field('Teléfono (opcional)', textControl({ id: 'h-phone', inputmode: 'tel', placeholder: '+502 5555 0199', autocomplete: 'off', value: h?.phone ?? '' }))}
      </fieldset>
      <fieldset><legend>Solicitud</legend>
        ${field(
          '¿Es persona expuesta políticamente (PEP)?',
          `${selectControl({ id: 'h-pep' }, `${opt('', 'Sin responder', pep === undefined)}${opt('no', 'No', pep === false)}${opt('si', 'Sí', pep === true)}`)}<small class="hint">Seguros G&amp;T la pide en gastos médicos y vida.</small>`,
        )}
        ${
          line === 'AUTO'
            ? field(
                'Número de inspección CERTIAUTO',
                `${textControl({ id: 'h-inspection', maxlength: 40, autocomplete: 'off', value: inspectionDraft ?? '' })}<small class="hint">Seguros G&amp;T lo pide para emitir Todo Riesgo.</small>`,
              )
            : ''
        }
      </fieldset>
      ${
        !me?.agentId && sellingAgents.list.length
          ? `<fieldset><legend>Venta</legend>
        ${field(
          'Agente que vende',
          selectControl({ id: 'h-agent' }, [
            ...(sellingAgents.selected ? [] : [{ value: '', label: 'Elige al agente' }]),
            ...sellingAgents.list.map((a) => ({
              value: a,
              label: cap(a),
              selected: a === sellingAgents.selected,
            })),
          ]),
          { required: agentRequired() },
        )}
      </fieldset>`
          : ''
      }
    </div>${issueFoot(p)}</form>`;
}

/** The policyholder as typed, checked the way the carriers check it. */
export function holderInput(): Holder {
  const fullName = textInput('#h-name', 'el nombre del contratante');
  const nationalId = textInput('#h-cui', 'el DPI del contratante').replace(/[\s-]/g, '');
  if (!/^\d{13}$/.test(nationalId)) throw new InputError('El DPI (CUI) lleva 13 dígitos.', 'h-cui');
  const taxId = textInput('#h-nit', 'el NIT del contratante').replace(/\s/g, '').toUpperCase();
  if (!/^\d{1,12}-[\dK]$/.test(taxId))
    throw new InputError(
      'Escribe el NIT con guion antes del último dígito, por ejemplo 1234567-8.',
      'h-nit',
    );
  const email = textInput('#h-email', 'el correo del contratante');
  const typed = ($('#h-phone') as HTMLInputElement).value.replace(/[\s-]/g, '');
  const phone = typed && !typed.startsWith('+') ? `+502${typed}` : typed;
  if (phone && !/^\+502\d{8}$/.test(phone))
    throw new InputError('El teléfono lleva 8 dígitos, con o sin +502.', 'h-phone');
  // Checked here, as the API would (AGENT_REQUIRED), only when the form asks it (#235, A2).
  if (document.querySelector('#h-agent') && agentRequired() && !sellingAgent())
    throw new InputError('Elige en «Agente que vende» a quién se le asigna la póliza.', 'h-agent');
  const pep = document.querySelector<HTMLSelectElement>('#h-pep')?.value ?? '';
  return {
    fullName,
    nationalId,
    taxId,
    email,
    ...(phone ? { phone } : {}),
    ...(pep ? { politicallyExposed: pep === 'si' } : {}),
  };
}

/**
 * Marks the policyholder's field an error names (aria-invalid, the error line next to it,
 * linked by aria-describedby) and takes the focus there, in sight (#235, A2); clears the mark of
 * the previous attempt. False when the error names no field of the form.
 */
function markHolderField(e: unknown): boolean {
  const form = document.querySelector('#holder-form');
  if (!form) return false;
  for (const el of form.querySelectorAll('[aria-invalid="true"]')) {
    el.removeAttribute('aria-invalid');
    el.removeAttribute('aria-describedby');
  }
  for (const el of form.querySelectorAll('.field-error')) el.remove();
  const id =
    e instanceof InputError
      ? e.field
      : e instanceof ApiError && (e.code === 'AGENT_REQUIRED' || e.code === 'UNKNOWN_AGENT')
        ? 'h-agent'
        : undefined;
  const field = id ? form.querySelector<HTMLElement>(`#${id}`) : null;
  if (!field) return false;
  const error = document.createElement('small');
  error.className = 'field-error';
  error.id = `${id}-err`;
  error.textContent = explain(e).human;
  (field.closest('.field') ?? field).append(error);
  field.setAttribute('aria-invalid', 'true');
  field.setAttribute('aria-describedby', error.id);
  // Fixed by the person: the mark goes as soon as the field changes.
  const clear = () => {
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    error.remove();
  };
  field.addEventListener('change', clear, { once: true });
  field.scrollIntoView({ block: 'center' });
  field.focus({ preventScroll: true });
  return true;
}

/** The CERTIAUTO inspection number typed for an auto policy (null: none). */
export function inspectionInput(): string | null {
  const typed = document.querySelector<HTMLInputElement>('#h-inspection')?.value.trim() ?? '';
  return typed || null;
}

/**
 * «Emitir» when nothing stands in the way; «Preparar emisión» when the carrier needs the
 * underwriting file first (a referred risk, or documents before issuing), with its progress.
 * Its accessible name says what it issues (`what`: plan, carrier and premium, #234). «Emitir»
 * chooses the plan: it opens the policyholder's block, whose «Emitir póliza» issues it (#238).
 */
export function optionAction(
  carrier: string,
  option: string,
  best: boolean,
  uw: UwOption[] | null,
  indicative = false,
  what = '',
) {
  const u = uw?.find((x) => x.carrier === carrier && x.option === option);
  const named = (label: string, choose = false) =>
    what ? ` aria-label="${esc(choose ? `${label}: elegir ${what}` : `${label} de ${what}`)}"` : '';
  const data = `data-carrier="${esc(carrier)}" data-option="${esc(option)}"`;
  if (u?.requiredBecause || u?.file) {
    const label = u.file
      ? u.file.readyToIssue
        ? 'Expediente listo'
        : `Expediente ${u.file.progress.done}/${u.file.progress.total}`
      : 'Preparar emisión';
    return `<button type="button" class="btn sm ${best ? 'primary' : ''}" data-prepare="${esc(carrier)}" ${data}${named(label)}>${esc(label)}</button>`;
  }
  const label = indicative ? 'Pedir emisión' : 'Emitir';
  return `<button type="button" class="btn sm ${best ? 'primary' : ''}" data-choose="${esc(carrier)}" ${data}${indicative ? ' data-indicative="1"' : ''}${named(label, true)}>${label}</button>`;
}

function quoteResults(
  quotes: CarrierQuote[],
  previous: CarrierQuote[] | null = null,
  version = 1,
  bound = false,
  fields: QField[] = [],
  uw: UwOption[] | null = null,
  detail: QuoteOptionsView | null = null,
  picked: string[] = [],
) {
  /** The option's coverages and premium broken down, and its «Comparar» toggle (#314). */
  const more = (carrier: string, option: string) => {
    const c = detail?.carriers.find((x) => x.carrier === carrier);
    const o = c?.options.find((x) => x.option === option);
    return c && o ? optionDetail(c, o) : '';
  };
  const pickButton = (carrier: string, option: string, label: string) => {
    if (!detail?.carriers.some((x) => x.carrier === carrier)) return '';
    const on = picked.includes(optionKey(carrier, option));
    return `<button type="button" class="btn sm compare-pick" data-compare-pick="${esc(optionKey(carrier, option))}" aria-pressed="${on}" aria-label="${esc(`Comparar ${label}`)}">${on ? `${icon('ok')}En la comparación` : 'Comparar'}</button>`;
  };
  /** Premium change of one option against the previous version (requote). */
  const delta = (carrier: string, option: string, now: Money | undefined) => {
    const before = previous
      ?.find((c) => c.carrier === carrier)
      ?.result?.options.find((o) => o.carrierOptionCode === option)?.premium?.gross;
    if (!before || !now) return '';
    const d = now.amountMinor - before.amountMinor;
    if (d === 0) return `<div class="muted small">igual que v${version - 1}</div>`;
    return `<div class="small delta ${d > 0 ? 'up' : 'down'}">${d > 0 ? '+' : '−'}${fmt({ ...now, amountMinor: Math.abs(d) })} vs v${version - 1}</div>`;
  };
  const minor = (o: Option) => o.premium?.gross.amountMinor ?? Number.POSITIVE_INFINITY;
  const cheapest = (c: CarrierQuote) =>
    Math.min(
      ...(c.result?.options.filter((o) => o.premium).map(minor) ?? []),
      Number.POSITIVE_INFINITY,
    );
  const sorted = [...quotes].sort((a, b) => cheapest(a) - cheapest(b));
  // "Mejor prima" compares like with like: plans priced in quetzales only.
  const bestMinor = Math.min(
    ...quotes.flatMap((c) =>
      c.result?.decision === 'ACCEPT'
        ? c.result.options
            .filter(
              (o) => o.premium && o.decision !== 'DECLINE' && o.premium.gross.currency === 'GTQ',
            )
            .map(minor)
        : [],
    ),
  );
  const priced = quotes.filter((c) => c.result?.options.some((o) => o.premium)).length;
  const cards = sorted
    .map((c) => {
      if (c.status === 'WORK_ORDER')
        return `<article class="card"><div class="card-head"><span class="monogram lg" aria-hidden="true">${esc(initials(c.name))}</span>
          <div class="carrier-name"><h3>${esc(c.name)}</h3><small>cotiza por su mesa de servicio</small></div><span class="spacer"></span>
          ${pill('info', 'Orden de trabajo')}</div>
          <p class="note">${icon('bell')}<span>${esc(c.workOrder?.message ?? 'Se abrió una orden de cotización.')} Su respuesta llega por <strong>Órdenes</strong>.</span></p></article>`;
      if (c.status !== 'QUOTED' || !c.result)
        return `<article class="card"><div class="card-head"><span class="monogram lg" aria-hidden="true">${esc(initials(c.name))}</span>
          <div class="carrier-name"><h3>${esc(c.name)}</h3><small>sin cotización</small></div><span class="spacer"></span>
          ${pill(c.status === 'MISSING_FIELDS' ? 'warn' : 'bad', c.status === 'MISSING_FIELDS' ? 'Faltan datos' : 'Sin respuesta')}</div>
          <p class="note ${c.status === 'MISSING_FIELDS' ? '' : 'bad'}">${icon('alert')}<span>${esc(
            c.status === 'MISSING_FIELDS'
              ? `Pide datos que faltan: ${(c.missing ?? []).map((p) => fieldLabel(p, fields)).join(', ')}`
              : `La aseguradora no respondió: ${c.error ?? 'error desconocido'}`,
          )}</span></p></article>`;
      const r = c.result;
      const options = r.options.filter((o) => o.premium);
      // A carrier the platform no longer offers the agency (#283): its saved quote is history.
      const closed = !offeredNow(c.carrier);
      const testing = inTesting(c.carrier) ? '<span class="badge testing">En pruebas</span>' : '';
      const closedNote = closed
        ? `<p class="note bad" data-component="carrier-not-offered">${icon('lock')}<span>${esc(c.name)} ya no cotiza ni emite pólizas nuevas en Nubi: puedes ver esta cotización, pero no emitirla. Lo ya emitido con ${esc(c.name)} sigue igual.</span></p>`
        : '';
      const reasons = r.reasons.length
        ? `<p class="note ${r.decision === 'DECLINE' ? 'bad' : ''}">${icon('alert')}<span>${r.decision === 'DECLINE' ? 'No acepta el riesgo' : r.decision === 'REFER' ? 'Requiere revisión' : 'Condiciones'}: ${r.reasons
            .map(
              (x) =>
                `${esc(REASON[x.category] ?? x.category)} <span class="mono">(${esc(x.carrierCode)})</span>`,
            )
            .join(', ')}</span></p>`
        : '';
      // Per-person answers (health): only those that are not a plain acceptance.
      const members = (r.members ?? []).filter(
        (m) => m.decision !== 'ACCEPT' || m.exclusions.length,
      );
      const membersNote = members.length
        ? `<p class="note">${icon('users')}<span>${members
            .map(
              (m) =>
                `Asegurado ${esc(m.personId.replace(/^\D+/, ''))}: ${esc(DECISION[m.decision] ?? m.decision)}${m.exclusions.length ? ` · excluye ${esc(m.exclusions.join(', ').toLowerCase())}` : ''}`,
            )
            .join('; ')}</span></p>`
        : '';
      const table = options.length
        ? `<div class="table-wrap" data-component="table"><table class="table quote"><thead><tr><th>Plan</th><th class="num">Prima anual</th><th class="num">Cuotas</th><th class="num">Tu comisión</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
          ${options
            .map((o) => {
              const best = minor(o) === bestMinor && r.decision === 'ACCEPT';
              return `<tr class="${best ? 'best' : ''}"><td><span class="option-name">${esc(planName(c.carrier, o.carrierOptionCode))}${best ? '<span class="badge">Mejor prima</span>' : ''}</span></td>
              <td class="num"><span class="premium">${fmt(o.premium?.gross)}</span>${delta(c.carrier, o.carrierOptionCode, o.premium?.gross)}</td>
              <td class="num">${o.premium?.installments.count} × ${fmt(o.premium?.installments.regular)}</td>
              <td class="num">${fmt(o.commission)}</td>
              <td class="actions">${pickButton(c.carrier, o.carrierOptionCode, `${planName(c.carrier, o.carrierOptionCode)} con ${c.name}`)}${r.decision === 'DECLINE' || bound || closed ? '' : optionAction(c.carrier, o.carrierOptionCode, best, uw, !!r.indicative, `${planName(c.carrier, o.carrierOptionCode)} con ${c.name}, ${fmt(o.premium?.gross)} al año`)}</td></tr>${(() => {
                const d = more(c.carrier, o.carrierOptionCode);
                return d ? `<tr class="option-more"><td colspan="5">${d}</td></tr>` : '';
              })()}`;
            })
            .join('')}</tbody></table></div>`
        : '';
      // An indicative price comes from the carrier's published rules, never from the carrier.
      const indicative = r.indicative
        ? `<p class="note info">${icon('alert')}<span>Precio indicativo según las reglas publicadas de ${esc(c.name)} (versión ${esc(r.ruleSet?.version ?? '—')}); la aseguradora confirma al emitir.</span></p>`
        : '';
      return `<article class="card ${r.indicative ? 'indicative' : ''}"><div class="card-head"><span class="monogram lg" aria-hidden="true">${esc(initials(c.name))}</span>
        <div class="carrier-name"><h3>${esc(c.name)}</h3><small>${r.indicative ? 'Referencia' : 'Cotización'} válida hasta el ${esc(date(r.validUntil))}</small></div>
        <span class="spacer"></span>${testing}${r.indicative ? '<span class="badge indicative">Indicativa</span>' : ''}${pill(r.decision)}</div>${closedNote}${indicative}${table}${reasons}${membersNote}</article>`;
    })
    .join('');
  return `<section class="quotes" aria-live="polite"><div class="section-head"><h2 id="quote-results-title" tabindex="-1">Resultados</h2>
    <span class="muted small">${priced} de ${quotes.length} aseguradoras con precio · ordenado por prima</span></div>${cards}</section>`;
}

/**
 * After «Emitir», the focus goes to the new policy in Pólizas (#234): Pólizas' own
 * `focusPolicy` unfolds its card and focuses it, searching the list by its number when it is
 * not among the policies loaded (never the first card of the list). Without the new period's id,
 * the focus goes to the screen.
 */
async function focusNewPolicy(scheduleId: string | undefined) {
  if (!scheduleId) {
    window.scrollTo({ top: 0 });
    $('#main').focus({ preventScroll: true });
    return;
  }
  await focusPolicy(scheduleId);
  const card = document.activeElement;
  if (card instanceof HTMLElement && card.matches('[data-policy]')) scrollBelowBar(card);
}

/** Scrolls `el` to the top of the screen, below the phone's top bar (the sidebar under 880 px). */
function scrollBelowBar(el: HTMLElement) {
  const bar = $('#sidebar').getBoundingClientRect();
  const covered = bar.width >= window.innerWidth - 1 ? Math.max(0, bar.bottom) : 0;
  const top = window.scrollY + el.getBoundingClientRect().top - covered - 16;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

/**
 * What the quote form sends: the answers to the line's questionnaire (autos included), which
 * the API turns into the canonical request.
 */
async function quoteBody(): Promise<Record<string, unknown>> {
  const line = (await loadCatalog()).find((l) => l.productLine === quoteLine);
  if (!line) throw new InputError('Elige un producto.');
  quoteDraft = readAnswers(line);
  return {
    productLine: line.productLine,
    answers: quoteDraft,
    effectiveDate: ($('#q-start') as HTMLInputElement).value,
    paymentFrequency: ($('#q-freq') as HTMLSelectElement).value,
  };
}

/** Repaints the generated form (after adding a person or with the API's errors). */
async function repaintQuestionnaire() {
  const line = (await loadCatalog()).find((l) => l.productLine === quoteLine);
  if (line && document.querySelector('#q-dynamic'))
    $('#q-dynamic').innerHTML = questionnaireForm(line, quoteDraft ?? {});
}

type UwOption = {
  carrier: string;
  option: string;
  decision: string;
  requiredBecause: 'REFER' | 'CARRIER_REQUIRES_DOCUMENTS' | null;
  file: {
    id: string;
    stage: string;
    readyToIssue: boolean;
    progress: { done: number; total: number };
  } | null;
};
type UwStamp = { at: string; by: string };
export type UwFile = {
  id: string;
  quoteId: string;
  version: number;
  carrier: string;
  option: string;
  productLine: string;
  label: string;
  agentId: string | null;
  decision: string;
  required: boolean;
  requiredBecause: 'REFER' | 'CARRIER_REQUIRES_DOCUMENTS' | null;
  stage: string;
  complete: boolean;
  readyToIssue: boolean;
  progress: { done: number; total: number };
  missing: { key: string; text: string }[];
  documents: {
    code: string;
    label: string;
    source: 'PRODUCT' | 'CARRIER';
    status: string;
    upload: { documentId: string; fileName: string; size: number; at: string } | null;
    review: (UwStamp & { reason: string | null }) | null;
  }[];
  declaration: {
    insured: {
      personId: string;
      label: string;
      asked: { conditions?: string[]; hospitalizedLast5Years?: boolean; smoker?: boolean };
    }[];
    answers: {
      personId: string;
      conditions: string[];
      hospitalizedLast5Years: boolean;
      underTreatment: boolean;
      treatmentDetail: string | null;
      smoker: boolean;
    }[];
    signature: (UwStamp & { signerName: string }) | null;
    inconsistencies: { personId: string; field: string }[];
    consentText: string;
  } | null;
  inspection: {
    slots: {
      slot: string;
      photo: { documentId: string; at: string; takenAt: string | null } | null;
    }[];
  } | null;
  biometric: {
    matched: boolean;
    attempts: (UwStamp & {
      fullName: string;
      dpiLast4: string;
      match: boolean;
      reference: string;
      reason: string | null;
    })[];
  };
  referral: {
    orderId: string;
    sent: UwStamp;
    resolution: {
      decision: string;
      surchargePercent: number | null;
      exclusions: string[];
      note: string | null;
    } | null;
    withdrawn: unknown;
  } | null;
};
/** The file on screen, and the policyholder typed on the quote (it carries over). */
export let uwFileId: string | null = null;
export let holderDraft: Partial<Holder> | null = null;
/** The CERTIAUTO number typed on the quote (it survives repaints like the policyholder). */
let inspectionDraft = '';

/** Keeps what was typed in the policyholder form across repaints. */
export function keepHolderDraft() {
  if (!document.querySelector('#h-name')) return;
  const val = (s: string) => ($(s) as HTMLInputElement).value.trim();
  const pep = document.querySelector<HTMLSelectElement>('#h-pep')?.value ?? '';
  holderDraft = {
    fullName: val('#h-name'),
    nationalId: val('#h-cui').replace(/[\s-]/g, ''),
    taxId: val('#h-nit'),
    email: val('#h-email'),
    phone: val('#h-phone'),
    ...(pep ? { politicallyExposed: pep === 'si' } : {}),
  };
  inspectionDraft = inspectionInput() ?? '';
}

/** The prospect being quoted from «Cotizar» (the quote is recorded on it). */
let quoteLead: { id: string; name: string } | null = null;
/** What the prospect already told us, to prefill the policyholder when issuing its quote. */
let quoteHolder: Partial<Holder> | null = null;

/** The customer being quoted from «Cotizar para este cliente» (the quote is linked to it). */
let quoteParty: { id: string; name: string } | null = null;

/** What other views change of the quote on screen (a customer's or a prospect's quote, issuing). */
type Quoting = {
  lastQuote: {
    request: QuoteReq;
    quotes: CarrierQuote[];
    quoteId: string;
    number?: number | null;
    version: number;
    previous: CarrierQuote[] | null;
    bound: string | null;
  } | null;
  quoteLine: string;
  quoteDraft: Answers | null;
  fieldErrors: Record<string, string>;
  quoteLead: { id: string; name: string } | null;
  quoteHolder: Partial<Holder> | null;
  quoteParty: { id: string; name: string } | null;
  holderDraft: Partial<Holder> | null;
  uwFileId: string | null;
};
export function setQuoting(s: Partial<Quoting>) {
  if (s.lastQuote !== undefined) lastQuote = s.lastQuote;
  if (s.quoteLine !== undefined) quoteLine = s.quoteLine;
  if (s.quoteDraft !== undefined) quoteDraft = s.quoteDraft;
  if (s.fieldErrors !== undefined) fieldErrors = s.fieldErrors;
  if (s.quoteLead !== undefined) quoteLead = s.quoteLead;
  if (s.quoteHolder !== undefined) quoteHolder = s.quoteHolder;
  if (s.quoteParty !== undefined) quoteParty = s.quoteParty;
  if (s.holderDraft !== undefined) holderDraft = s.holderDraft;
  if (s.uwFileId !== undefined) uwFileId = s.uwFileId;
}

/** A new session: no quote, catalog, drafts, file, customer or prospect of the one before. */
export function resetQuoting() {
  lastQuote = null;
  catalog = null;
  quoteLine = 'AUTO';
  quoteDraft = null;
  fieldErrors = {};
  sellingAgents = { list: [], selected: null };
  uwFileId = null;
  holderDraft = null;
  chosenPlan = null;
  quoteLead = null;
  quoteHolder = null;
  quoteParty = null;
  quoteFilter = { ...NO_FILTER };
  picks = { of: '', keys: [] };
  comparison = null;
  versionCompared = null;
}

/** Adds or removes an item of a repeatable group (an insured person, a claim). */
const editGroup: ClickAction = (_, d) => {
  void loadCatalog().then(async (lines) => {
    const line = lines.find((l) => l.productLine === quoteLine);
    if (!line) return;
    const answers = readAnswers(line);
    const group = (d.addItem ?? d.removeItem) as string;
    const items = Array.isArray(answers[group]) ? (answers[group] as Answers[]) : [];
    if (d.addItem) items.push({});
    else items.splice(Number(d.index), 1);
    answers[group] = items;
    quoteDraft = answers;
    fieldErrors = {};
    await repaintQuestionnaire();
    const blocks = document.querySelectorAll<HTMLElement>(
      `[data-group-item="${CSS.escape(group)}"]`,
    );
    (
      (d.addItem ? blocks[blocks.length - 1] : blocks[0])?.querySelector<HTMLElement>(
        'wa-input, wa-number-input, wa-select, wa-checkbox',
      ) ?? document.querySelector<HTMLElement>(`[data-add-item="${CSS.escape(group)}"]`)
    )?.focus();
  });
};

/** The quote on screen, for its tools (null when none). */
const onScreen = () => lastQuote;
const quotePath = (id: string, suffix: string) => `/v1/quotes/${encodeURIComponent(id)}${suffix}`;
const pickedQuery = () =>
  `version=${lastQuote?.version ?? 1}&options=${encodeURIComponent(picks.keys.join(','))}`;

/** Searches the list again with what its bar says now, from the first page. */
function searchQuotes(focus: string) {
  const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value ?? '';
  quoteFilter = {
    q: value('qf-q').trim(),
    status: value('qf-status'),
    productLine: value('qf-line'),
    carrier: value('qf-carrier'),
    agentId: value('qf-agent'),
  };
  delete listPages.cotizaciones;
  void render('cotizar', { quiet: true }).then(() => $(`#${focus}`)?.focus());
}

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  click: {
    /** «Comparar» of an option: in or out of the comparison (up to four). */
    comparePick: (_, d) => {
      const key = d.comparePick;
      if (!key) return;
      if (picks.keys.includes(key)) picks.keys = picks.keys.filter((k) => k !== key);
      else if (picks.keys.length >= MAX_PICKS) {
        toast(`Compara hasta ${MAX_PICKS} opciones: quita una antes de agregar otra.`, 'error');
        return;
      } else picks.keys = [...picks.keys, key];
      comparison = null;
      void render('cotizar', { quiet: true }).then(() =>
        document.querySelector<HTMLElement>(`[data-compare-pick="${CSS.escape(key)}"]`)?.focus(),
      );
    },
    quoteCompare: (t) => {
      const quote = onScreen();
      if (!quote || !picks.keys.length) return;
      void act(t, 'Comparando las opciones…', async () => {
        comparison = await api<Comparison>(
          'GET',
          quotePath(quote.quoteId, `/comparison?${pickedQuery()}`),
        );
        await render('cotizar', { quiet: true });
        document.querySelector<HTMLElement>('[data-component="comparison"]')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return `${comparison.columns.length} opciones lado a lado.`;
      });
    },
    proposalDownload: (t) => {
      const quote = onScreen();
      if (!quote || !picks.keys.length) return;
      void act(t, 'Preparando la propuesta…', async () => {
        const name = await download(
          quotePath(quote.quoteId, `/proposal?${pickedQuery()}`),
          'propuesta.pdf',
        );
        return `«${name}» descargada.`;
      });
    },
    proposalSend: (t, d) => {
      const quote = onScreen();
      const channel = d.proposalSend;
      if (!quote || !channel || !picks.keys.length) return;
      void act(t, 'Enviando la propuesta al cliente…', async () => {
        try {
          await api('POST', quotePath(quote.quoteId, '/proposal'), {
            channel,
            version: quote.version,
            options: picks.keys.map((k) => {
              const [carrier = '', option = ''] = k.split(':');
              return { carrier, option };
            }),
          });
        } catch (e) {
          if (e instanceof ApiError && e.code === 'NO_CONSENT')
            throw new InputError(
              'El cliente no aceptó mensajes por ese medio en su ficha: descarga la propuesta y entrégala tú.',
            );
          throw e;
        }
        await render('cotizar', { quiet: true });
        return `Propuesta enviada por ${CHANNEL_LABEL[channel] ?? channel}. Queda en las Comunicaciones del cliente.`;
      });
    },
    versionCompare: (t, d) => {
      const quote = onScreen();
      const to = Number(d.versionCompare);
      if (!quote || !(to > 1)) return;
      void act(t, 'Comparando las versiones…', async () => {
        versionCompared = await api<VersionComparison>(
          'GET',
          quotePath(quote.quoteId, `/versions/compare?from=${to - 1}&to=${to}`),
        );
        await render('cotizar', { quiet: true });
        return `Versión ${to - 1} frente a la ${to}.`;
      });
    },
    carrierQuoteDoc: (t, d) => {
      const id = d.carrierQuoteDoc;
      if (!id) return;
      void act(t, 'Pidiendo el PDF a la aseguradora…', async () => {
        const name = await download(
          `/v1/schedules/${encodeURIComponent(id)}/documents/QUOTE`,
          'cotizacion-aseguradora.pdf',
        );
        return `«${name}» descargado.`;
      });
    },
    quoteFilterClear: () => {
      quoteFilter = { ...NO_FILTER };
      delete listPages.cotizaciones;
      void render('cotizar', { quiet: true }).then(() => $('#qf-q')?.focus());
    },
    newQuote: () => {
      lastQuote = null;
      // A new quote is for someone else: the policyholder typed for the last one stays behind.
      holderDraft = null;
      quoteHolder = null;
      quoteParty = null;
      quoteDraft = null;
      fieldErrors = {};
      void render('cotizar');
    },
    addItem: editGroup,
    removeItem: editGroup,
    openQuote: (t, d) => {
      if (!d.openQuote) return;
      void act(t, 'Abriendo la cotización…', async () => {
        const q = await api<{
          id: string;
          number?: number | null;
          agentId: string | null;
          binding: { policyNumber: string } | null;
          versions: { number: number; request: QuoteReq; answers: CarrierQuote[] }[];
        }>('GET', `/v1/quotes/${encodeURIComponent(d.openQuote ?? '')}`);
        const latest = q.versions[q.versions.length - 1];
        if (!latest) throw new InputError('La cotización no tiene versiones.');
        quoteDraft = null;
        fieldErrors = {};
        lastQuote = {
          request: latest.request,
          quotes: latest.answers,
          quoteId: q.id,
          number: q.number ?? null,
          version: latest.number,
          previous: q.versions[q.versions.length - 2]?.answers ?? null,
          bound: q.binding?.policyNumber ?? null,
          agentId: q.agentId,
        };
        await render('cotizar', { quiet: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return `Cotización ${q.number ? quoteNumber(q.number) : shortId(q.id)} abierta en su versión ${latest.number}.`;
      });
    },
    /** «Emitir» of a plan in the comparison: chooses it and asks for the policyholder (#238). */
    choose: (_, d) => {
      if (!d.choose || !lastQuote || lastQuote.bound) return;
      // What was typed in the block of another plan stays (and survives a repaint).
      keepHolderDraft();
      chosenPlan = {
        quoteId: lastQuote.quoteId,
        version: lastQuote.version,
        carrier: d.choose,
        option: d.option ?? '',
      };
      showHolder();
    },
    /** Back to the comparison, to choose another plan. */
    changePlan: () => {
      const results = $('#quote-results-title');
      scrollBelowBar(results);
      results.focus({ preventScroll: true });
    },
    prepare: (t, d) => {
      if (!d.prepare || !lastQuote) return;
      const quote = lastQuote;
      // The policyholder typed on the quote carries over to the file.
      keepHolderDraft();
      if (!holderDraft?.fullName) holderDraft = holderOf(quote.request) ?? holderDraft;
      void act(t, 'Abriendo el expediente…', async () => {
        const f = await api<UwFile>(
          'POST',
          `/v1/quotes/${encodeURIComponent(quote.quoteId)}/underwriting-files`,
          { version: quote.version, carrier: d.prepare, option: d.option },
        );
        uwFileId = f.id;
        await render('expediente');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return f.readyToIssue
          ? `Expediente ${f.id} listo para emitir.`
          : `Expediente ${f.id}: faltan ${f.missing.length} requisitos.`;
      });
    },
    bind: (t, d) => {
      if (!d.bind || !lastQuote) return;
      const quote = lastQuote;
      void act(t, 'Emitiendo con la aseguradora…', async () => {
        let policyholder: Holder;
        try {
          policyholder = holderInput();
        } catch (e) {
          if (!markHolderField(e))
            $('#holder-form').scrollIntoView({ behavior: 'smooth', block: 'start' });
          throw e;
        }
        markHolderField(null);
        const body = {
          carrier: d.bind,
          request: quote.request,
          option: d.option,
          agentId: me?.agentId ?? sellingAgent(),
          quoteId: quote.quoteId,
          version: quote.version,
          policyholder,
          ...(inspectionInput() ? { vehicleInspectionRef: inspectionInput() } : {}),
        };
        // «Emitir» again while the carrier reviews the solicitud follows that same solicitud
        // (the same key), never presents another one (#219).
        const scope = issuanceScope({
          quoteId: quote.quoteId,
          version: quote.version,
          carrier: d.bind ?? '',
          option: d.option ?? '',
        });
        const r = await followRequest(scope, (idempotencyKey) =>
          api<{ policyNumber?: string; scheduleId?: string; status?: string; message?: string }>(
            'POST',
            '/v1/policies',
            body,
            { idempotencyKey },
          ),
        ).catch((e: unknown) => {
          // The API's word on the agent (one the agency no longer has) goes to the field too.
          markHolderField(e);
          throw e;
        });
        lastQuote = null;
        chosenPlan = null;
        holderDraft = null;
        inspectionDraft = '';
        quoteHolder = null;
        quoteParty = null;
        // The carrier issues this line by hand: Nubi opened the work order (ADR-0006).
        if (r.status === 'WORK_ORDER') {
          await render('ordenes');
          // An indicative option: the carrier confirms the price its rules gave when it issues.
          const confirms = d.indicative
            ? ` ${carrierName(d.bind ?? '')} confirma el precio indicativo al emitir.`
            : '';
          return `${r.message ?? 'Se abrió la orden de emisión.'}${confirms} La póliza se registra al completarla con su número.`;
        }
        await render('polizas');
        await focusNewPolicy(r.scheduleId);
        return `Póliza ${r.policyNumber} emitida a nombre de ${policyholder.fullName}.`;
      });
    },
    partyQuoteDrop: () => {
      quoteParty = null;
      quoteHolder = null;
      void render('cotizar', { quiet: true });
    },
    leadQuoteDrop: () => {
      quoteLead = null;
      quoteHolder = null;
      void render('cotizar', { quiet: true });
    },
  },
  submit: {
    'quote-search-form': (ev) => {
      ev.preventDefault();
      searchQuotes('qf-q');
    },
    'quote-lost-form': (ev) => {
      ev.preventDefault();
      const quote = onScreen();
      if (!quote) return;
      const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value ?? '';
      const reason = value('ql-reason') || 'PRICE';
      const note = value('ql-note').trim();
      if (reason === 'OTHER' && !note) {
        toast('Escribe por qué se perdió la cotización.', 'error');
        $('#ql-note')?.focus();
        return;
      }
      void act($('#ql-submit'), 'Cerrando la cotización…', async () => {
        await api('POST', quotePath(quote.quoteId, '/lost'), { reason, note: note || null });
        await render('cotizar', { quiet: true });
        return `Cotización cerrada como perdida: ${QUOTE_LOST_REASON[reason] ?? reason}.`;
      });
    },
    'holder-form': (ev) => {
      ev.preventDefault();
    },
    'quote-form': (ev) => {
      ev.preventDefault();
      const requote = !!lastQuote && !lastQuote.bound;
      let leadNote = '';
      void act($('#quote-submit'), 'Cotizando con todas tus aseguradoras…', async () => {
        const body = await quoteBody();
        type Quoted = {
          quoteId: string;
          number?: number | null;
          version: number;
          request: QuoteReq;
          quotes: CarrierQuote[];
          previous?: CarrierQuote[];
          lead?: { id: string; stage: string };
        };
        const lead = requote ? null : quoteLead;
        let r: Quoted;
        try {
          r =
            requote && lastQuote
              ? await api<Quoted>(
                  'POST',
                  `/v1/quotes/${encodeURIComponent(lastQuote.quoteId)}/versions`,
                  { ...body, reason: textInput('#q-reason', 'el motivo de la recotización') },
                )
              : await api<Quoted>('POST', '/v1/quotes', {
                  ...body,
                  ...(lead ? { leadId: lead.id } : {}),
                  ...(quoteParty ? { partyId: quoteParty.id } : {}),
                });
        } catch (e) {
          // The questionnaire's errors go next to each field; the toast says how many.
          if (e instanceof ApiError && e.errors.length) {
            fieldErrors = Object.fromEntries(e.errors.map((x) => [x.path, x.message]));
            await repaintQuestionnaire();
            document.querySelector<HTMLElement>('#q-dynamic [aria-invalid="true"]')?.focus();
            throw new InputError(
              e.errors.length === 1
                ? e.errors[0]?.message
                : `Revisa ${e.errors.length} datos del formulario.`,
            );
          }
          throw e;
        }
        lastQuote = {
          request: r.request,
          quotes: r.quotes,
          quoteId: r.quoteId,
          number: r.number ?? null,
          version: r.version,
          previous: r.previous ?? null,
          bound: null,
        };
        if (r.lead && lead) {
          quoteLead = null;
          leadNote = ` ${lead.name} quedó como prospecto cotizado.`;
        }
        quoteDraft = null;
        fieldErrors = {};
        const n = r.quotes.filter((c) => c.result?.options.some((o) => o.premium)).length;
        await render('cotizar', { quiet: true });
        // The focus follows the repaint to the results, in sight (#234), as error-summary.ts does.
        const results = $('#quote-results-title');
        scrollBelowBar(results);
        results.focus({ preventScroll: true });
        const note = leadNote;
        leadNote = '';
        return requote
          ? `Versión ${lastQuote.version} guardada: ${n} de ${lastQuote.quotes.length} aseguradoras cotizaron.`
          : `${n} de ${lastQuote.quotes.length} aseguradoras cotizaron. Cotización guardada.${note}`;
      });
    },
  },
  change: {
    'qf-status': () => searchQuotes('qf-status'),
    'qf-line': () => searchQuotes('qf-line'),
    'qf-carrier': () => searchQuotes('qf-carrier'),
    'qf-agent': () => searchQuotes('qf-agent'),
    'q-line': (sel) => {
      // Another product is a new quote: its own questionnaire, nothing carried over.
      quoteLine = sel.value;
      lastQuote = null;
      quoteDraft = null;
      fieldErrors = {};
      void render('cotizar', { quiet: true }).then(() => $('#q-line').focus());
    },
  },
};
